import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';

import {
  Organization,
  newOrganization,
} from '../../../common/snrwbCore/contexts/OrganizationContext';
import { OrganizationSelector } from '../Organizations/OrganizationSelector';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { useAuth } from '../../../common/hooks/useAuth';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { Role } from '../../../common/snrwbCore/authorization/snrwbAuthorizationRoles';
import { CreateUpdateSampleCollectDto } from '../../../common/snrwbCore/autogenerated/snrwbApiClient';
import { newSampleCollect } from '../../../common/snrwbCore/contexts/SampleCollectContext';

import { SampleCollectCreateForm } from './SampleCollectCreateForm';

interface AddSampleCollectProps {
  onCompleted: (
    organization: Organization,
    sampleCollect: CreateUpdateSampleCollectDto,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  className?: string;
  organization?: Organization;
  buttonText?: string;
  hideClearOrganizationButton?: boolean;
}

export const AddSampleCollect: React.FC<AddSampleCollectProps> = props => {
  const [show, setShow] = useState(false);
  const [organizationValid, setOrganizationValid] = useState(false);
  const [sampleCollectValid, setSampleCollectValid] = useState(false);
  const [showOrganizationErrors, setShowOrganizationErrors] = useState(false);
  const [showSampleCollectErrors, setShowSampleCollectErrors] = useState(false);
  const [organization, setOrganization] = useState<Organization>(
    props.organization || newOrganization(),
  );
  const [sampleCollect, setSampleCollect] = useState(newSampleCollect());
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const auth = useAuth();
  const notifications = useNotifications();

  const handleClose = () => setShow(false);

  const handleShow = () => {
    if (!auth.check(Role.ExamsEdit)) {
      notifications.unauthorized();
      return;
    }
    setShow(true);
  };

  const createSampleCollect = () => {
    if (!organizationValid) {
      setShowOrganizationErrors(true);
      return;
    }
    if (!sampleCollectValid) {
      setShowSampleCollectErrors(true);
      return;
    }

    props.onCompleted(organization, sampleCollect).then(saveStatus => {
      if (!saveStatus.saved) {
        setSaveErrors(saveStatus.errors);
        setShowSaveErrors(true);
        return;
      }

      setShow(false);
    });
  };

  const onChangeSampleCollect = (
    sampleCollect: CreateUpdateSampleCollectDto,
    valid: boolean,
  ) => {
    setSampleCollect(sampleCollect);
    setSampleCollectValid(valid);
  };

  useEffect(() => {
    if (organizationValid) {
      setShowOrganizationErrors(false);
    }
    if (sampleCollectValid) {
      setShowSampleCollectErrors(false);
    }
  }, [organizationValid, sampleCollectValid]);

  useEffect(() => {
    setOrganizationValid(false);
    setSampleCollectValid(false);
    setShowOrganizationErrors(false);
    setShowSampleCollectErrors(false);
    setOrganization(props.organization || newOrganization());
    setSampleCollect(newSampleCollect());
    setShowSaveErrors(false);
    setSaveErrors([]);
  }, [show, props.organization]);

  return (
    <>
      <Button
        variant="outline-primary"
        onClick={handleShow}
        className={props.className}
      >
        {props.buttonText || 'Nowe pobranie próbek'}
      </Button>

      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered={true}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Tworzenie nowego pobrania próbek</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col className="col-lg-6 col-12 mb-1">
              <Card>
                <Card.Header>Podmiot, u którego pobrano próbki</Card.Header>
                <Card.Body>
                  <OrganizationSelector
                    onSelect={organization => setOrganization(organization)}
                    onValidation={setOrganizationValid}
                    onClear={() => {
                      setOrganization(props.organization || newOrganization());
                      setOrganizationValid(false);
                    }}
                    value={organization}
                    hideClearButton={props.hideClearOrganizationButton}
                    readOnly={props.organization ? true : false}
                    showValidationErrors={showOrganizationErrors}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col className="col-lg-6 col-12 mb-1">
              <Card>
                <Card.Header>Pobranie próbek</Card.Header>
                <Card.Body aria-disabled="true">
                  <SampleCollectCreateForm
                    value={sampleCollect}
                    organization={organization}
                    showValidationErrors={showSampleCollectErrors}
                    onChange={onChangeSampleCollect}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            Zamknij
          </Button>
          <Button variant="outline-primary" onClick={createSampleCollect}>
            Utwórz
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
